import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18next from 'i18next';

import { checkoutAPIService } from '~/api/checkout';
import { authenticationAPIService } from '~/api/login';
import { IS_ACTIVE_SUBSCRIPTION_STATUS, IS_TRIAL_SUBSCRIPTION_STATUS } from '~/constants/common';
import { PricePlan } from '~/types/checkout';

// --- Types ---
interface AccountData {
  languageCode?: string;
  [key: string]: any;
}

interface TaxData {
  [key: string]: any;
}

interface SubscriptionInfo {
  status?: string;
  plan?: PricePlan;
  [key: string]: any;
}

interface AccountState {
  data: AccountData;
  taxData: TaxData;
  isUriValid: boolean | null;
  loading: boolean;
  error: any;
  isUpdating: boolean;
  updated: boolean;
  isSubscriptionValid: boolean;
  isTrialSubscription: boolean;
  subscriptionInfo: SubscriptionInfo;
  loginPriceValue: number | null;
}

const initialState: AccountState = {
  data: {},
  taxData: {},
  isUriValid: null,
  loading: false,
  error: null,
  isUpdating: false,
  updated: false,
  isSubscriptionValid: true,
  isTrialSubscription: false,
  subscriptionInfo: {},
  loginPriceValue: null,
};

// --- Thunks ---
export const getAccountThunk = createAsyncThunk<AccountData, void, { rejectValue: any }>(
  'accountSlice/getAccountThunk',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticationAPIService.getAccount();

      if (response?.data?.languageCode) {
        i18next.changeLanguage(response?.data?.languageCode);
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccountThunk = createAsyncThunk<AccountData, Record<string, any>, { rejectValue: any }>(
  'accountSlice/updateAccountThunk',
  async (body, { rejectWithValue }) => {
    try {
      const response = await authenticationAPIService.updateAccount(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getAccountTaxInfoThunk = createAsyncThunk<TaxData, void, { rejectValue: any }>(
  'accountSlice/getAccountTaxInfoThunk',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticationAPIService.getAccountTaxSettings();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateAccountTaxInfoThunk = createAsyncThunk<TaxData, Record<string, any>, { rejectValue: any }>(
  'accountSlice/updateAccountTaxInfoThunk',
  async (body, { rejectWithValue }) => {
    try {
      const response = await authenticationAPIService.updateAccountTaxSettings(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getSubscriptionInfoThunk = createAsyncThunk<SubscriptionInfo, void, { rejectValue: any }>(
  'accountSlice/getSubscriptionInfoThunk',
  async (_, { rejectWithValue }) => {
    try {
      const response = await checkoutAPIService.checkSubscription();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  },
);

// --- Slice ---
export const accountSlice = createSlice({
  name: 'accountSlice',
  initialState,
  reducers: {
    setState(state, action: PayloadAction<AccountState>) {
      return action.payload;
    },
    setData(state, action: PayloadAction<AccountData>) {
      state.data = action.payload;
    },
    setLoginPriceValue(state, action: PayloadAction<number | null>) {
      state.loginPriceValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // account get
      .addCase(getAccountThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAccountThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getAccountThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // account update
      .addCase(updateAccountThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAccountThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.updated = true;
        state.data = action.payload;
      })
      .addCase(updateAccountThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // get tax-information
      .addCase(getAccountTaxInfoThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(getAccountTaxInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.taxData = action.payload;
      })
      .addCase(getAccountTaxInfoThunk.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      // update tax-information
      .addCase(updateAccountTaxInfoThunk.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateAccountTaxInfoThunk.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.updated = true;
        state.taxData = action.payload;
      })
      .addCase(updateAccountTaxInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // get subscription-info
      .addCase(getSubscriptionInfoThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscriptionInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isSubscriptionValid = action.payload.status === IS_ACTIVE_SUBSCRIPTION_STATUS;
        state.isTrialSubscription = action.payload.plan === IS_TRIAL_SUBSCRIPTION_STATUS;
        state.subscriptionInfo = action.payload;
      })
      .addCase(getSubscriptionInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setState, setData, setLoginPriceValue } = accountSlice.actions;

export default accountSlice.reducer;
